// FILE: src/components/SessionGuard.jsx
// Global session watcher, mounted once by MainLayout (plan WP-2 task 10):
// - polls GET /api/session/status every 30 s and when the tab regains focus
//   (polling is passive on the server, so it never keeps a session alive);
// - opens the identity challenge the server says is due, on any page (B-5);
// - warns at 14 idle minutes and signs out at 15 (MTO 1.4); real activity
//   pings POST /api/session/keepalive at most every 30 s so the server's
//   idle clock matches the student's;
// - break / daily-cap dialogs (TimeNotices, fed by the time slice) and the
//   enrolment expiry banner. Every figure comes from the server (WP-3).
// Session-ending codes (idle, replaced, locked, expired) are handled in
// fetchWithAuth, which signs the student out and lets /login explain why.
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Link
} from "@mui/material";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector, useStore } from "react-redux";
import fetchWithAuth from "../utils/fetchWithAuth";
import useIdleTimer from "../hooks/useIdleTimer";
import { showChallenge } from "../features/course2faSlice";
import { sessionEnded } from "../features/authSlice";
import { apiFetch } from "../services/api";
import CourseVerificationDialog from "./CourseVerificationDialog";
import { BreakDialog, DailyCapDialog } from "./TimeNotices";
import { timeStatusReceived } from "../features/timeSlice";
import IconDialogTitle from "./student/IconDialogTitle";

export const POLL_MS = 30_000;
export const KEEPALIVE_MIN_MS = 30_000;
export const WARN_AFTER_MS = 14 * 60_000;
export const IDLE_AFTER_MS = 15 * 60_000;

export default function SessionGuard() {
  const dispatch = useDispatch();
  const store = useStore();
  const [warningOpen, setWarningOpen] = useState(false);
  const lastPing = useRef(0);
  const daysLeft = useSelector((s) => s.time.daysLeft);
  const isStudent = useSelector((s) => s.auth.role !== "admin");

  const apply = useCallback(
    (s) => {
      if (!s) return;
      if (s.role !== "admin") dispatch(timeStatusReceived(s));
      if (s.challengeDue && s.challenge && !store.getState().course2fa.required) {
        dispatch(showChallenge(s.challenge));
      }
    },
    [dispatch, store]
  );

  const poll = useCallback(() => {
    fetchWithAuth("/api/session/status").then(apply).catch(() => {});
  }, [apply]);

  const keepalive = useCallback(
    (force = false) => {
      const now = Date.now();
      if (!force && now - lastPing.current < KEEPALIVE_MIN_MS) return;
      lastPing.current = now;
      fetchWithAuth("/api/session/keepalive", { method: "POST" }).then(apply).catch(() => {});
    },
    [apply]
  );

  useEffect(() => {
    poll();
    const t = setInterval(poll, POLL_MS);
    const onVisible = () => document.visibilityState === "visible" && poll();
    window.addEventListener("focus", poll);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", poll);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [poll]);

  useIdleTimer({
    warningTimeout: WARN_AFTER_MS,
    sessionTimeout: IDLE_AFTER_MS,
    onWarn: () => setWarningOpen(true),
    onActive: () => keepalive(false),
    onIdle: () => {
      setWarningOpen(false);
      dispatch(sessionEnded({ code: "SESSION_IDLE" }));
      apiFetch("/api/auth/logout", { method: "POST" }).catch(() => {});
    }
  });

  const stay = () => {
    setWarningOpen(false);
    keepalive(true);
  };

  const signOutNow = () => {
    setWarningOpen(false);
    dispatch(sessionEnded({ code: null }));
    apiFetch("/api/auth/logout", { method: "POST" }).catch(() => {});
  };

  return (
    <>
      {isStudent && daysLeft != null && daysLeft <= 30 && (
        <Alert
          severity={daysLeft <= 7 ? "error" : "warning"}
          icon={<EventBusyOutlinedIcon />}
          sx={{ mb: 3 }}
          role="status"
          data-testid="enrolment-banner"
        >
          {daysLeft > 0 && <AlertTitle>Your enrolment is ending soon</AlertTitle>}
          {daysLeft > 0
            ? `Your enrolment ends in ${daysLeft} ${daysLeft === 1 ? "day" : "days"}. Finish the course before then.`
            : "Your enrolment period has ended. Contact student support for help."}{" "}
          <Link component={RouterLink} to="/student/support" sx={{ color: "inherit", fontWeight: 600 }}>
            {daysLeft > 0 ? "Need more time? Contact student support" : "Go to Support"}
          </Link>
        </Alert>
      )}

      <CourseVerificationDialog />

      {/* Escape = stay signed in (the safe choice); focus starts there too. */}
      <Dialog
        open={warningOpen}
        onClose={stay}
        fullWidth
        maxWidth="xs"
        aria-labelledby="idle-title"
        aria-describedby="idle-description"
      >
        <IconDialogTitle id="idle-title" icon={TimerOutlinedIcon} tone="warning">
          Are you still there?
        </IconDialogTitle>
        <DialogContent>
          <DialogContentText id="idle-description" sx={{ color: "text.primary" }}>
            You haven't been active for 14 minutes. For your security you'll be signed out after 15 minutes
            without activity.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, flexWrap: "wrap", gap: 1, "& > :not(style) ~ :not(style)": { ml: 0 } }}>
          <Button onClick={signOutNow}>Sign out now</Button>
          <Button variant="contained" onClick={stay} autoFocus>
            Stay signed in
          </Button>
        </DialogActions>
      </Dialog>

      {isStudent && (
        <>
          <BreakDialog onCountdownEnd={poll} />
          <DailyCapDialog />
        </>
      )}
    </>
  );
}
